import DashboardContainer from "components/dashboard/DashboardContainer";
import { useWindowSize } from "hooks/useWindowSize";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { TableUI } from "components/design/TableUI";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import { Chip } from "components/DesignSystem/Chips/Chips";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import { HighlightSearchTerm } from "components/DesignSystem/HighlightText";
import Modal from "components/DesignSystem/Modal/Modal";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import { SelectionCell } from "components/DesignSystem/SelectionCell/SelectionCell";
import { SelectionHeaderCell } from "components/DesignSystem/SelectionHeaderCell/SelectionHeaderCell";
import { MyProducts as ProductAndServicesIcon } from "components/icons/LeftNav/Books/MyProducts";
import { OptionDropdown } from "components/icons/optionDropdown";
import { NoProductAndServices } from "components/Illustrations/NoProductAndServices";
import {
  sourceFilterOptions,
  typeFilterOptions,
} from "constants/productAndServices";
import {
  REVENUE_DATA_SOURCE,
  REVENUE_DATA_SOURCE_ICON,
  REVENUE_DATA_SOURCE_OPTIONS,
} from "constants/revenueDataSource";
import { PRODUCT_SERVICE_ITEM_TYPE } from "dictionaries";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useFilters } from "hooks/useFilter";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { useModal } from "hooks/useModal";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import React, { useMemo, useState } from "react";
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import {
  createColumnHelper,
  getCoreRowModel,
  RowSelectionState,
  SortingState,
  useReactTable,
} from "react-table-8.10.7";
import {
  useDeleteProductAndServiceMutation,
  useGetProductAndServicesQuery,
  useGetProductCategoriesQuery,
} from "store/apis/productAndServices";
import { ProductAndServicesList as ProductAndServicesListType } from "types/Models/productAndServices";
import { BackendError } from "types/utils/error";
import { useDebounce } from "utils/debounce";
import { ProductFormModalRoot } from "./ProductFormModal";
import { ProductOrServiceSlider } from "./ProductOrServiceSlider";
import { ProductBulkActions } from "./ProductBulkActions";
import { Contacts } from "components/icons/LeftNav/Books/Contacts";

const SELECTION_COLUMN_ID = "SELECTION";

type ProductAndServiceActionProps = {
  productAndService: ProductAndServicesListType;
  hasWritePermission: boolean;
  onDeleted: () => void;
};

const ProductAndServiceAction = ({
  productAndService,
  hasWritePermission,
  onDeleted,
}: ProductAndServiceActionProps) => {
  const deleteModal = useModal();
  const editModal = useModal();
  const { alertToast, successToast } = useToast();
  const { uuid, source } = productAndService;
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const [deleteProductAndService, { isLoading: isDeleting }] =
    useDeleteProductAndServiceMutation();

  if (!hasWritePermission) return null;

  const onDelete = async () => {
    try {
      const { type } = await deleteProductAndService({
        groupId,
        entityId,
        productId: uuid,
      }).unwrap();
      deleteModal.close();
      onDeleted();

      successToast({ message: `${PRODUCT_SERVICE_ITEM_TYPE[type]} deleted!` });
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as {}
      );
    }
  };

  const isManual = source === REVENUE_DATA_SOURCE.MANUAL;

  return (
    <span onClick={(e) => e.stopPropagation()}>
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <span>
            <Button size="xsmall" customType="ghost_icon">
              <OptionDropdown color="currentColor" />
            </Button>
          </span>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content align="end" className="!t-min-w-40">
            <Dropdown.Item
              type="ordinary"
              onSelect={editModal.open}
              disabled={!isManual}
            >
              <ConditionalToolTip
                condition={
                  !isManual &&
                  `Cannot edit ${REVENUE_DATA_SOURCE_OPTIONS[source]} product/service`
                }
              >
                <span>Edit</span>
              </ConditionalToolTip>
            </Dropdown.Item>
            <Dropdown.Item
              type="danger"
              onSelect={deleteModal.open}
              disabled={!isManual}
            >
              <ConditionalToolTip
                condition={
                  !isManual &&
                  `Cannot delete ${REVENUE_DATA_SOURCE_OPTIONS[source]} product/serivce`
                }
              >
                <span>Delete</span>
              </ConditionalToolTip>
            </Dropdown.Item>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>

      <Modal.Root open={deleteModal.isOpen} onOpenChange={deleteModal.close}>
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Are you sure?</Modal.Title>
            <Modal.Close disabled={isDeleting} />
          </Modal.Header>
          <Modal.Body>
            The selected item will be permanently deleted. This action cannot be
            undone.
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild disabled={isDeleting}>
              <Button>Cancel</Button>
            </Modal.RawClose>
            <Button
              customType="danger"
              onClick={onDelete}
              isLoading={isDeleting}
              disabled={isDeleting}
            >
              Confirm
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
      <ProductFormModalRoot
        isOpen={editModal.isOpen}
        close={editModal.close}
        mode="edit"
        itemId={uuid}
      />
    </span>
  );
};

export const ProductAndServicesList = () => {
  const { url, path } = useRouteMatch();
  const history = useHistory();
  const { search } = useLocation();
  const query = useQuery();
  const { update } = useUpdateQuery();
  const page = query.get("page");
  const entityId = useCurrentEntityId();
  const { isMobile } = useWindowSize();
  const { uuid: groupId } = useCurrentGroupContext();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.REVENUE
  );

  const searchValue = query.get("search");
  const searchTerm = useDebounce(searchValue);

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    update({ query: "search", value: e.target.value });
  };

  const { data: categories = [] } = useGetProductCategoriesQuery();

  const filterOptionsWithCategories = {
    SOURCES: sourceFilterOptions,
    TYPE: typeFilterOptions,
    CATEGORY: categories.map((category) => ({
      value: category.uuid,
      label: category.name,
    })),
  };

  const { values: filterValues, updateFilter } = useFilters({
    initialValue: {
      SOURCES: [] as string[],
      TYPE: [] as string[],
      CATEGORY: [] as string[],
    },
  });

  const appliedFilterCount = useMemo(
    () =>
      Object.values(filterValues).reduce((acc, value) => acc + value.length, 0),
    [filterValues]
  );

  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const sortCol = sorting[0]?.id;
  const sortOrder = sorting[0]?.desc;

  const { data, isLoading, isSuccess, isFetching } =
    useGetProductAndServicesQuery(
      {
        groupId,
        entityId,
        pageNum: Number(page) || 1,
        searchTerm,
        sources: filterValues.SOURCES.join(","),
        productTypes: filterValues.TYPE.join(","),
        productCategories: filterValues.CATEGORY.join(","),
        sortCol: sortCol ? (sortCol as "PRICE" | "NAME") : undefined,
        sortOrder: sortOrder ? ("DSC" as const) : ("ASC" as const),
      },
      {
        skip: !entityId || !groupId,
      }
    );

  const {
    products = [],
    per_page = 1,
    total_count = 1,
    total_pages = 1,
  } = data || {};

  const createColumn = createColumnHelper<ProductAndServicesListType>();

  const columns = [
    createColumn.display({
      id: SELECTION_COLUMN_ID,
      size: 3,
      enableSorting: false,
      header: ({ table }) => (
        <SelectionHeaderCell table={table} ariaLabel="Select all items" />
      ),
      cell: ({ row }) => (
        <SelectionCell row={row} ariaLabel={`Select ${row.original.name}`} />
      ),
    }),

    createColumn.accessor("uuid", {
      size: 1,
      header: "No.",
      enableSorting: false,
      cell: ({ row }) => {
        return row.index + 1;
      },
    }),

    createColumn.accessor("name", {
      size: 22,
      header: "Name",
      id: "NAME",
      enableSorting: true,
      cell: (row) => {
        return (
          <HighlightSearchTerm
            text={row.getValue()}
            searchTerm={searchTerm || ""}
          />
        );
      },
    }),

    createColumn.accessor("type", {
      size: 8,
      header: "Type",
      enableSorting: false,
      cell: (row) => {
        return PRODUCT_SERVICE_ITEM_TYPE[row.getValue()];
      },
    }),

    createColumn.accessor("product_category_name", {
      size: 25,
      header: "Group",
      enableSorting: false,
      cell: (row) => {
        return row.getValue() || "-";
      },
    }),

    createColumn.accessor("default_price", {
      size: 12,
      header: () => (
        <span className="t-flex t-justify-end t-ml-auto">Default Price</span>
      ),
      enableSorting: true,
      id: "PRICE",
      cell: (info) => {
        const { linked_price_list_count } = info.row.original;

        return (
          <div className="t-flex t-flex-col t-items-end t-whitespace-nowrap">
            <AmountSuperScript amount={info.getValue()} />
            {linked_price_list_count > 1 && (
              <span className="t-text-body-sm t-text-text-30">
                {linked_price_list_count} prices
              </span>
            )}
          </div>
        );
      },
    }),
    createColumn.accessor("logo_url", {
      size: 10,
      header: () => <div className="t-flex-1 t-text-center">Source</div>,
      enableSorting: false,
      cell: (info) => {
        const { source } = info.row.original;

        return (
          <div className="t-flex t-justify-center">
            <ConditionalToolTip condition={REVENUE_DATA_SOURCE_OPTIONS[source]}>
              <span>
                {source === REVENUE_DATA_SOURCE.MANUAL ? (
                  <span
                    className="t-rounded-full t-bg-white t-overflow-hidden t-min-h-6 t-min-w-6 t-max-h-6 t-max-w-6 t-text-neutral-40 t-border-neutral-0 t-border t-border-solid t-text-center"
                    role="img"
                  >
                    <Contacts />
                  </span>
                ) : (
                  <Avatar
                    size="small"
                    src={REVENUE_DATA_SOURCE_ICON[source]}
                    alt={source}
                  />
                )}
              </span>
            </ConditionalToolTip>
          </div>
        );
      },
    }),

    createColumn.accessor("uuid", {
      size: 5,
      id: "actions",
      header: "",
      enableSorting: false,
      cell: (info) => {
        return (
          <ProductAndServiceAction
            productAndService={info.row.original}
            hasWritePermission={hasWritePermission}
            onDeleted={() => info.row.toggleSelected(false)}
          />
        );
      },
    }),
  ];

  const table = useReactTable({
    data: products,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      size: 10,
      minSize: 1,
      maxSize: 100,
    },
    manualSorting: true,
    enableMultiSort: false,
    enableSortingRemoval: false,
    onSortingChange: (newSorting) => {
      setSorting(newSorting);
    },
    state: {
      sorting,
      rowSelection,
      columnVisibility: {
        [SELECTION_COLUMN_ID]: hasWritePermission,
      },
    },
    getRowId: (row) => row.uuid,
    enableRowSelection: (row) =>
      row.original.source === REVENUE_DATA_SOURCE.MANUAL,
    onRowSelectionChange: setRowSelection,
  });

  const selectedProductIds = Object.keys(rowSelection).filter(
    (id) => rowSelection[id]
  );

  const clearSelection = () => setRowSelection({});

  const handleProductRowClick = (uuid: string) =>
    history.push(`${url}/${uuid}${search}`);

  return (
    <>
      <DashboardContainer className="t-relative t-gap-5">
        <DashboardContainer.Header className="t-sticky t-top-0 t-z-header t-bg-white">
          <div className="t-flex t-gap-4 sm:t-flex-col sm:t-gap-2">
            <div className="t-flex t-gap-2 t-w-full t-items-center">
              <Search
                isLoading={isFetching}
                className="t-w-[18%] focus:t-w-3/6 t-transition-all t-self-start sm:t-w-auto sm:t-min-w-0 sm:t-flex-1 sm:focus:t-w-auto"
                onChange={onSearch}
                defaultValue={searchValue || ""}
                placeholder="Search"
              />
              <Filter.Root
                defaultValue="TYPE"
                hideDivider={isMobile}
                title={
                  appliedFilterCount > 0 ? (
                    <span className="t-text-text-30">
                      ({appliedFilterCount})
                    </span>
                  ) : null
                }
                capsule={
                  <span className="t-contents sm:t-hidden">
                    {filterValues.SOURCES.length > 0 && (
                      <Chip
                        onRemove={() => updateFilter("SOURCES", [])}
                        isRemovable
                        isActive
                        filterType="SOURCES"
                      >
                        {`Sources (${filterValues.SOURCES.length})`}
                      </Chip>
                    )}

                    {filterValues.TYPE.length > 0 && (
                      <Chip
                        onRemove={() => updateFilter("TYPE", [])}
                        isRemovable
                        isActive
                        filterType="TYPE"
                      >
                        {`Type (${filterValues.TYPE.length})`}
                      </Chip>
                    )}

                    {filterValues.CATEGORY.length > 0 && (
                      <Chip
                        onRemove={() => updateFilter("CATEGORY", [])}
                        isRemovable
                        isActive
                        filterType="CATEGORY"
                      >
                        {`Category (${filterValues.CATEGORY.length})`}
                      </Chip>
                    )}
                  </span>
                }
              >
                <Filter.Portal>
                  <Filter.List>
                    <Filter.ListItem value="TYPE">Type</Filter.ListItem>
                    <Filter.ListItem value="CATEGORY">Category</Filter.ListItem>
                    <Filter.ListItem value="SOURCES">Source</Filter.ListItem>
                  </Filter.List>
                  {Object.keys(filterValues).map((key) => {
                    const filter = key as keyof typeof filterValues;
                    return (
                      <Filter.Body value={filter} block key={filter}>
                        <MultiSelectFilter
                          onChange={(newValues) =>
                            updateFilter(filter, newValues)
                          }
                          isSearchable={filter === "CATEGORY"}
                          options={filterOptionsWithCategories[filter]}
                          selected={filterValues[filter]}
                        />
                      </Filter.Body>
                    );
                  })}
                </Filter.Portal>
              </Filter.Root>
            </div>
            <div className="t-flex t-justify-end t-self-start sm:t-self-end">
              <PaginationHandler
                page_num={Number(page) || 1}
                per_page={per_page}
                total_count={total_count}
                total_pages={total_pages}
                isLoading={isFetching}
              />
            </div>
          </div>
        </DashboardContainer.Header>
        <DashboardContainer.Content>
          <Async.Root
            isLoading={isLoading}
            isSuccess={isSuccess}
            isEmpty={products.length === 0}
          >
            <Async.Empty>
              <EmptyScreen
                text=""
                cta={
                  <>
                    {searchTerm && (
                      <Button
                        onClick={() => update({ query: "search", value: "" })}
                        size="small"
                        customType="primary"
                      >
                        Clear search
                      </Button>
                    )}
                  </>
                }
              >
                <div className="t-text-center t-flex t-gap-2 t-flex-col t-items-center">
                  <NoProductAndServices />
                  {searchTerm ? (
                    <div className="t-flex t-flex-col t-gap-2 t-justify-center t-items-center">
                      <p className="t-m-0 t-text-subtitle-sm">
                        No results found for your search
                      </p>
                      <p className="t-m-0 t-text-body-sm">
                        Try clearing the search.
                      </p>
                    </div>
                  ) : (
                    <div className="t-flex t-flex-col t-gap-2 t-justify-center t-items-center">
                      <p className="t-m-0 t-text-subtitle-sm">
                        No results found
                      </p>
                      <p className="t-m-0 t-text-body-sm">
                        Try clearing the filters.
                      </p>
                    </div>
                  )}
                </div>
              </EmptyScreen>
            </Async.Empty>
            <Async.Success>
              <TableUI
                table={table}
                onRowClick={(row) => handleProductRowClick(row.original.uuid)}
                enableSort
                isHeaderCellUpperCase={false}
              />
            </Async.Success>
          </Async.Root>
        </DashboardContainer.Content>
      </DashboardContainer>
      <ProductBulkActions
        groupId={groupId}
        entityId={entityId}
        selectedProductIds={selectedProductIds}
        onClearSelection={clearSelection}
      />
      <Switch>
        <Route path={`${path}/:productOrserviceId`}>
          <ProductOrServiceSlider
            onClose={() => history.push(`${url}${search}`)}
          />
        </Route>
      </Switch>
    </>
  );
};
